/**
 * Badge that surfaces a feature's plan status.
 *
 * Adapts to the org's current plan via {@link useEntitlements}:
 *  - Ungated feature -> renders nothing (no catalog entry).
 *  - Gated + locked  -> "Upgrade" pill, click opens FeatureUpgradeModal.
 *  - Gated + unlocked -> tiny check icon ("Included on <plan>" tooltip),
 *    click opens FeatureUpgradeModal in celebratory mode so the user can see
 *    what their subscription includes.
 *
 * Call sites can render this unconditionally for any gated feature; the
 * component decides whether (and how) to draw itself.
 *
 * @module components/ui/FeatureGateBadge
 */

import React, { useState, useCallback } from 'react';
import { FeatureUpgradeModal } from './FeatureUpgradeModal';
import { getFeatureCopy } from '../../lib/featureCatalog';
import { useEntitlements } from '../../lib/useEntitlements';
import type { FeatureKey, PlanTier } from '../../lib/entitlements';

/** Props for the FeatureGateBadge component. */
interface FeatureGateBadgeProps {
  /** Feature key used to look up the required plan and upgrade copy. */
  feature: FeatureKey;
}

/** Capitalised display labels for each plan tier. */
const PLAN_DISPLAY_NAMES: Record<PlanTier, string> = {
  free: 'Free',
  pro: 'Pro',
  business: 'Business',
  enterprise: 'Enterprise',
};

/**
 * Plan-aware feature pill.
 *
 * Stops click propagation so the badge can be safely nested inside interactive
 * parents (e.g. a Toggle's label, a card-style button).
 */
export const FeatureGateBadge: React.FC<FeatureGateBadgeProps> = ({ feature }) => {
  const copy = getFeatureCopy(feature);
  const { can, tier } = useEntitlements();
  const [modalOpen, setModalOpen] = useState(false);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setModalOpen(true);
  }, []);

  const handleClose = useCallback(() => setModalOpen(false), []);

  if (!copy) return null;

  const unlocked = can(feature);
  const planLabel = PLAN_DISPLAY_NAMES[tier];

  if (unlocked) {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          title={`Included on ${planLabel}`}
          className="group inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-success text-success-foreground light:bg-success-soft light:text-white hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success transition-colors cursor-pointer"
        >
          {/*
           * Open padlock - body anchors at the bottom; the shackle is pivoted
           * up and to the right, ending in mid-air so the lock reads as
           * unmistakably "unclicked" (not a closed cabinet). Lucide's
           * `unlock` glyph, which is the canonical UI shorthand for this state.
           */}
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 9.9-1" />
          </svg>
          Included
        </button>
        <FeatureUpgradeModal
          open={modalOpen}
          onClose={handleClose}
          feature={feature}
          unlocked
          currentPlanLabel={planLabel}
        />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        title={`Upgrade to ${copy.requiredPlan}`}
        className="group inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-success text-success-foreground light:bg-success-soft light:text-white hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success transition-colors cursor-pointer"
      >
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2.25l1.813 4.687a4.5 4.5 0 002.625 2.625L21.125 11.25l-4.687 1.813a4.5 4.5 0 00-2.625 2.625L12 20.375l-1.813-4.687a4.5 4.5 0 00-2.625-2.625L2.875 11.25l4.687-1.813a4.5 4.5 0 002.625-2.625L12 2.25z" />
        </svg>
        Upgrade
      </button>
      <FeatureUpgradeModal open={modalOpen} onClose={handleClose} feature={feature} />
    </>
  );
};
